/**
 * I documenti del programma partner: la forma condivisa da server, console e partner.yumaos.com.
 *
 * Doc canonico: `docs/01-ARCHITECTURE/PROGRAMMA-PARTNER-MODEL.md` (§4). Convenzione campi: snake_case.
 *
 * ⚠️ Nel gestionale `partner_tenants` esiste già e vuol dire un'altra cosa (aziende in conto terzi): queste raccolte
 * sono di piattaforma (`platform_partner*`) e non hanno niente a che fare con quel campo.
 *
 * ⚠️ Tutto ciò che riguarda soldi, abbinamenti e accessi lo scrive SOLO il server: le regole Firestore lo vietano al
 * browser, console compresa. Le date di calendario sono `AAAA-MM-GG` (UTC, vedi `compensoPartner.ts`); gli istanti
 * sono Timestamp di Firestore, qui `unknown` perché il tipo cambia fra SDK del browser e del server.
 *
 * @since 2026-10-19
 */

import type { ProfiloCompensoDati, RegimeFiscaleDati, ScaglioneCompenso } from './compensoPartner';

export const RACCOLTA_PARTNER = 'platform_partner';
export const RACCOLTA_PROFILI = 'platform_partner_profili';
export const RACCOLTA_REGIMI = 'platform_partner_regimi_fiscali';
export const RACCOLTA_ABBINAMENTI = 'platform_partner_abbinamenti';
export const RACCOLTA_MOVIMENTI = 'platform_partner_movimenti';
export const RACCOLTA_LIQUIDAZIONI = 'platform_partner_liquidazioni';
/** I link d'invito dei partner (`codiceInvito.ts`); l'id del documento è il codice. */
export const RACCOLTA_INVITI = 'platform_partner_inviti';
/** Le impostazioni del programma: giorni di una registrazione, frequenza predefinita dei pagamenti. */
export const DOC_IMPOSTAZIONI = 'platform_config/programma_partner';

// ============================================================================
// PARTNER
// ============================================================================

export type TipoSoggettoPartner = 'persona_fisica' | 'societa_di_persone' | 'societa_di_capitali';
export type StatoPartner = 'invitato' | 'attivo' | 'sospeso' | 'cessato';

/** `platform_partner/{partnerId}` */
export interface PartnerDoc {
  nome: string;
  email: string;
  /** Lingua delle email, degli avvisi e dell'app (ISO a 2 lettere). */
  lingua: string;
  /** Paese ISO a 2 lettere: decide il prefisso della partita IVA e i regimi proponibili. */
  paese: string;
  /**
   * Persona fisica o società. `null` finché non lo dichiara il partner (03/11/2026, come nei programmi partner fatti
   * bene: i dati fiscali e per i pagamenti li compila lui dal suo portale, prima del primo pagamento).
   */
  tipo_soggetto: TipoSoggettoPartner | null;
  ragione_sociale?: string;
  partita_iva?: string;
  codice_fiscale?: string;
  iban?: string;
  telefono?: string;
  /**
   * Il trattamento fiscale e previdenziale, scelto dalla piattaforma sui dati dichiarati dal partner. `null` = ancora da
   * scegliere: le provvigioni maturano, ma la liquidazione non si prepara (`liquidazioni.ts`).
   */
  regime_fiscale_id: string | null;
  /** Il profilo di compenso. `null` solo per chi è `senza_provvigioni`. */
  profilo_compenso_id: string | null;
  /**
   * Un partner che invita SENZA provvigioni: YumaOS stessa (03/11/2026, Simone: «anche noi stessi come proprietari della
   * piattaforma avremo uno di questi account … ma naturalmente nel nostro caso non ci saranno provvigioni»). Stesse
   * funzioni d'invito; le aziende che porta restano sue (nessun altro partner le prende), ma niente movimenti, niente
   * liquidazioni, niente dati per i pagamenti.
   */
  senza_provvigioni?: boolean;
  /** Ogni quanti mesi si paga; assente = il predefinito della piattaforma. */
  liquidazione_ogni_mesi?: number;
  /** La sua scheda nella Rete della console, se c'è. */
  persona_rete_id?: string | null;
  /** L'account con cui entra: lo scrive il server quando il partner accetta l'invito. */
  uid?: string | null;
  /**
   * Un codice breve del partner, scritto alla creazione. ⚠️ NON è il link d'invito: dal 03/11/2026 ogni invito ha il
   * suo codice (`platform_partner_inviti`), così il partner sa chi si è iscritto da quale invito e può spegnerne uno.
   */
  codice: string;
  stato: StatoPartner;
  /**
   * L'invito: si conserva solo l'IMPRONTA del codice (sha256), mai il codice, che viaggia solo nell'email. Chi apre il
   * link e entra con l'account dell'email invitata diventa il partner (`uid`).
   */
  invito?: { codice_hash: string; inviato_il: string; accettato_il?: string | null } | null;
  contratto?: { tipo: string; versione?: string; firmato_il?: string; percorso_file?: string } | null;
  /** Quando il partner ha dichiarato i suoi dati per i pagamenti dalla sua app (`AAAA-MM-GG`). */
  dati_dichiarati_il?: string | null;
  note?: string;
  created_at?: unknown;
  updated_at?: unknown;
}

// ============================================================================
// PROFILI E REGIMI (dati della console)
// ============================================================================

/** `platform_partner_profili/{id}`: i numeri non si cambiano (si crea un profilo nuovo); si spegne, non si cancella. */
export interface ProfiloCompensoDoc extends ProfiloCompensoDati {
  attivo: boolean;
  created_at?: unknown;
}

/** `platform_partner_regimi_fiscali/{id}` */
export interface RegimeFiscaleDoc extends RegimeFiscaleDati {
  attivo: boolean;
  created_at?: unknown;
}

// ============================================================================
// ABBINAMENTI: QUALE AZIENDA È DI QUALE PARTNER
// ============================================================================

export type OrigineAbbinamento = 'registrazione' | 'link' | 'a_mano';
/**
 * - `registrata`: il partner l'ha registrata e l'azienda non è ancora in YumaOS; vale fino a `scade_il`;
 * - `da_decidere`: un doppione (l'azienda è già in YumaOS): decide Simone (19/10/2026);
 * - `attiva`: l'azienda è in YumaOS e i suoi incassi maturano provvigioni;
 * - `rifiutata`, `scaduta`: non vale più; un altro partner può registrarla.
 */
export type StatoAbbinamento = 'registrata' | 'da_decidere' | 'attiva' | 'rifiutata' | 'scaduta';

/** L'esclusiva del fornitore decisa a mano dalla console: fino a quando, chi, quando, perché. */
export interface EsclusivaDecisaAMano {
  fino_al: string;
  deciso_da: string;
  deciso_il: string;
  motivo?: string;
}

/** Uno stato passato della stessa azienda, quando una registrazione nuova prende il posto di una che non vale più. */
export interface AbbinamentoPrecedente {
  partner_id: string;
  stato: StatoAbbinamento;
  registrata_il: string;
  motivo?: string;
}

/**
 * `platform_partner_abbinamenti/{chiave}`. La chiave è la partita IVA normalizzata (`chiavePartitaIva`) finché
 * l'azienda non esiste in YumaOS: così due registrazioni della stessa azienda sono lo stesso documento, e il doppione è
 * impossibile per costruzione. Quando l'azienda nasce, `tenant_id` si riempie.
 */
export interface AbbinamentoDoc {
  partner_id: string;
  origine: OrigineAbbinamento;
  stato: StatoAbbinamento;
  /** Ciò che ha scritto il partner registrandola. */
  azienda_nome: string;
  partita_iva_chiave: string;
  contatto?: { nome?: string; email?: string; telefono?: string } | null;
  note_partner?: string;
  tenant_id?: string | null;
  registrata_il: string;
  /** Fino a quando la registrazione vale se l'azienda non si abbona. */
  scade_il: string;
  /** Da quale giorno le fatture pagate contano per il partner (la registrazione, o la data scelta a mano). */
  contare_dal: string;
  /** Inizio del primo periodo di posti pagato: da qui si contano i mesi di abbonamento. */
  abbonata_dal?: string | null;
  /** Copia degli scaglioni del profilo al momento dell'abbinamento: cambiare il profilo non tocca questa azienda. */
  profilo_id: string | null;
  scaglioni: ScaglioneCompenso[];
  /** Se rifiutata o cambiata a mano, il perché (lo legge il partner). */
  motivo?: string;
  /** Il link d'invito da cui l'azienda si è iscritta (origine `link`, o una registrazione poi arrivata dal link). */
  invito_codice?: string | null;
  precedenti?: AbbinamentoPrecedente[];
  /**
   * L'esclusiva del fornitore decisa A MANO da Simone (atlante v2, cap. 444): l'ultimo giorno, che vince sulla regola dei
   * mesi (`mesi_esclusiva_fornitore`); un giorno nel passato = tolta. Assente o null = vale la regola. Conta solo se il
   * partner è un fornitore. La scrive solo `partnerConsoleEsclusiva`.
   */
  esclusiva_fornitore?: EsclusivaDecisaAMano | null;
  /** L'ultimo giro che ha letto le fatture di questa azienda su Stripe. */
  ultimo_controllo?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
}

// ============================================================================
// INVITI: I LINK CHE IL PARTNER MANDA
// ============================================================================

export type StatoInvito = 'attivo' | 'spento';

/**
 * `platform_partner_inviti/{codice}`. Lo crea e lo spegne il partner (dalla sua app, passando dal server); lo legge
 * il server quando un'azienda si iscrive col link. Un invito si può usare più volte (un link in un gruppo WhatsApp porta
 * più aziende): tutte vanno al partner.
 */
export interface InvitoDoc {
  partner_id: string;
  /** A chi l'ha mandato, scritto dal partner per ricordarselo: lo vede solo lui (e il super utente). */
  etichetta: string;
  stato: StatoInvito;
  creato_il: string;
  spento_il?: string | null;
  /** Quante aziende si sono iscritte da questo link e sono diventate sue. */
  iscrizioni: number;
  ultima_iscrizione_il?: string | null;
  created_at?: unknown;
  updated_at?: unknown;
}

// ============================================================================
// MOVIMENTI E LIQUIDAZIONI
// ============================================================================

export type TipoMovimento = 'maturata' | 'storno';

/** `platform_partner_movimenti/{idFattura}_{idRiga}` (storno: `{idFattura}_rimborso_{idRimborso}`). Immutabile. */
export interface MovimentoDoc {
  partner_id: string;
  abbinamento_id: string;
  tenant_id: string;
  azienda_nome: string;
  tipo: TipoMovimento;
  fattura_stripe_id: string;
  riga_stripe_id?: string;
  rimborso_stripe_id?: string;
  /** Giorno dell'incasso (o del rimborso): decide il periodo di liquidazione. */
  data: string;
  valuta: string;
  base_centesimi: number;
  percentuale: number;
  importo_centesimi: number;
  dal_mese?: number;
  al_mese?: number;
  /** Il periodo di liquidazione in cui cade (`AAAA-MM` dal–al). */
  periodo_dal: string;
  periodo_al: string;
  liquidazione_id?: string | null;
  created_at?: unknown;
}

export type StatoLiquidazione = 'bozza' | 'approvata' | 'fattura_ricevuta' | 'pagata';

/** `platform_partner_liquidazioni/{partnerId}_{periodoDal}_{periodoAl}_{valuta}` */
export interface LiquidazioneDoc {
  partner_id: string;
  periodo_dal: string;
  periodo_al: string;
  valuta: string;
  movimenti: number;
  imponibile_centesimi: number;
  iva_centesimi: number;
  ritenuta_centesimi: number;
  cassa_partner_centesimi: number;
  cassa_nostra_centesimi: number;
  netto_centesimi: number;
  /** Copia del regime usato per il conto. */
  regime: RegimeFiscaleDati;
  stato: StatoLiquidazione;
  fattura_partner?: { numero?: string; data?: string; percorso_file?: string } | null;
  pagata_il?: string | null;
  created_at?: unknown;
  updated_at?: unknown;
}

// ============================================================================
// IMPOSTAZIONI DEL PROGRAMMA
// ============================================================================

/** `platform_config/programma_partner` */
export interface ImpostazioniProgrammaPartner {
  giorni_registrazione: number;
  liquidazione_ogni_mesi: number;
  /** Per quanti mesi dall'iscrizione un'azienda portata da un fornitore resta sua (cap. 444). */
  mesi_esclusiva_fornitore: number;
}
